import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ListData, PaginatedResponse } from '../../../types';
import usePlayer from '../contexts/usePlayer';
import useToast from '../contexts/useToast';
import BlurFillImage from '../components/BlurFillImage';
import PlayButton from '../components/PlayButton';
import ItemActionButton from '../components/ItemActionButton';

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default function BuildersCanon() {
  const navigate = useNavigate();
  const { nowPlayingId } = usePlayer();
  const { showToast } = useToast();
  const [items, setItems] = useState<ListData[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingAll, setAddingAll] = useState(false);
  const [queueVersion, setQueueVersion] = useState(0);

  useEffect(() => {
    fetch('/api/category/builders-canon?limit=25', { credentials: 'include' })
      .then((res) => res.json())
      .then((data: PaginatedResponse<ListData>) => {
        setItems(data.items);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [queueVersion]);

  const totalSeconds = items.reduce(
    (sum, item) => sum + (item.secondsTotal || 0),
    0
  );

  const allQueued = items.length > 0 && items.every((item) => item.inQueue);

  const handleAddAll = async () => {
    if (addingAll || allQueued) return;
    setAddingAll(true);

    const toAdd = items.filter((item) => !item.inQueue);
    let added = 0;

    for (const item of toAdd) {
      try {
        const res = await fetch('/api/queue/add', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contentId: item.id }),
        });
        if (res.ok) added++;
      } catch {
        // continue with next
      }
    }

    showToast(
      `Added ${added} item${added !== 1 ? 's' : ''} to queue`,
      'success'
    );
    setAddingAll(false);
    setQueueVersion((v) => v + 1);
  };

  if (loading) {
    return (
      <div
        className="page"
        style={{ padding: '40px 10px', textAlign: 'center' }}
      >
        <p style={{ color: 'var(--primary-soft)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ padding: '32px 16px 24px', textAlign: 'center' }}>
        <h1
          style={{
            margin: 0,
            fontSize: '2rem',
            fontFamily: 'var(--font-display)',
            fontWeight: 400,
            color: 'var(--primary)',
          }}
        >
          The Builder's Canon
        </h1>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: '0.95rem',
            color: 'var(--primary-soft)',
            lineHeight: 1.5,
          }}
        >
          {items.length} essential essays for builders, optimists, and those who
          believe technology can make the future better.
        </p>
        {totalSeconds > 0 && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '0.85rem',
              color: 'var(--subtle)',
            }}
          >
            {formatDuration(totalSeconds)} total listening time
          </p>
        )}
        <button
          className="pill-button"
          onClick={handleAddAll}
          disabled={addingAll || allQueued}
          style={{
            marginTop: '16px',
            padding: '10px 24px',
            fontSize: '0.9rem',
            fontWeight: 500,
            borderColor: allQueued ? 'var(--gold-hover)' : 'var(--gold)',
            color: allQueued ? 'var(--gold-hover)' : 'var(--gold)',
            cursor: allQueued ? 'default' : 'pointer',
          }}
        >
          {addingAll
            ? 'Adding...'
            : allQueued
              ? 'All in queue'
              : 'Add all to queue'}
        </button>
      </div>

      {/* Item list */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '0 10px 120px',
        }}
      >
        {items.map((item) => {
          const isNowPlaying = nowPlayingId === item.id;
          return (
            <div
              key={item.id}
              onClick={() => navigate(`/id/${item.id}`)}
              style={{
                cursor: 'pointer',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              {item.imageUrl && (
                <BlurFillImage
                  imageUrl={item.imageUrl}
                  style={{ width: '100%', height: '180px' }}
                />
              )}
              <div style={{ padding: '16px' }}>
                {item.author && (
                  <p
                    style={{
                      margin: '0 0 4px 0',
                      fontSize: '0.8rem',
                      color: 'var(--accent)',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {item.author}
                  </p>
                )}
                <h3
                  style={{
                    margin: 0,
                    fontSize: '1.25rem',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 400,
                    color: 'var(--primary)',
                  }}
                >
                  {item.title}
                </h3>
                {item.description && (
                  <p
                    style={{
                      margin: '4px 0 8px 0',
                      fontSize: '0.9rem',
                      color: 'var(--primary-soft)',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.description}
                  </p>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: item.description ? '0' : '8px',
                  }}
                >
                  {!isNowPlaying && <PlayButton item={item} size={36} />}
                  <ItemActionButton
                    item={item}
                    onAdd={() => setQueueVersion((v) => v + 1)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
